import { Component, DestroyRef, EventEmitter, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from '../../../../../services/message.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  Subject,
} from 'rxjs';
import { Paginator } from '../../../../../models/paginator';
import {
  DiscoveredUrl,
  DiscoveredUrlsFilters,
  DiscoveredUrlStatus,
} from '../../../../../models/discoveredUrls';
import moment from 'moment';
import { Option } from '../../../../../components/select/select.component';
import { MAX_DISCOVERY_DEPTH } from '../../../../../models/integration/crawler';
import _ from 'lodash';
import {
  ConfirmationDialogComponent
} from '../../../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {
  DiscoveredUrlsService
} from '../../../../../services/discovered-urls.service';

@Component({
  selector: 'app-backlinks-discovery-list',
  templateUrl: './backlinks-discovery-list.component.html',
  styleUrls: ['./backlinks-discovery-list.component.scss']
})
export class BacklinksDiscoveryListComponent implements OnInit {
  private integrationId?: string;
  loading: boolean = false;
  done: boolean = false;

  protected readonly DiscoveredUrlStatus = DiscoveredUrlStatus;

  discoveredBacklinksPaginator: Paginator<DiscoveredUrl> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: 'createdAt _id'
  };

  filters: DiscoveredUrlsFilters = {
    query: undefined,
    depth: undefined,
  };

  selection = new SelectionModel<DiscoveredUrl>(true, []);
  selectionChange = new EventEmitter<SelectionModel<DiscoveredUrl>>();

  readonly depthSelectOptions: Option[] = Array.from(
    { length: MAX_DISCOVERY_DEPTH + 1 }, // include 0
    (_, i) => i.toString(10),
  )
    .map((depth) => ({
      label: depth,
      value: depth,
      selected: false,
      disabled: false,
    }));

  readonly displayedColumns: string[] = [
    'select',
    'url',
    'status',
    'depth',
    'createdAt',
    'actions'
  ];

  private readonly filtersSubject = new Subject<DiscoveredUrlsFilters>();

  constructor (
    private readonly _message: MessageService,
    private readonly dialog: MatDialog,
    private readonly discoveredUrlsService: DiscoveredUrlsService,
    private readonly route: ActivatedRoute,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    firstValueFrom(this.route.params)
      .then(async (params) => {
        this.integrationId = params['id'];
        this.loading = true;
        this.done = false;
        await this.retrieveDiscoveredUrls();
        this.loading = false;
        this.done = true;
      });

    this.filtersSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(async () => {
      this.resetPage();
      await this.retrieveDiscoveredUrls();
    });
  }

  async retrieveDiscoveredUrls() {
    if (!this.integrationId) return;

    try {
      this._message.emitChange('LOADING', 'START');

      this.discoveredBacklinksPaginator = {
        ...this.discoveredBacklinksPaginator,
        ...await this.discoveredUrlsService.retrieveBacklinksPaginated(
          this.integrationId,
          this.discoveredBacklinksPaginator.page,
          this.discoveredBacklinksPaginator.limit,
          this.discoveredBacklinksPaginator.offset,
          this.discoveredBacklinksPaginator.sort,
          this.filters,
        ),
      };
    } catch (err) {
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    } finally {
      this._message.emitChange('LOADING', 'END');
    }
  }

  resetPage() {
    this.discoveredBacklinksPaginator.limit = 10;
    this.discoveredBacklinksPaginator.page = 1;
    this.discoveredBacklinksPaginator.offset = 0;
  }

  async handlePage(event: PageEvent) {
    this.discoveredBacklinksPaginator.limit = event.pageSize;
    this.discoveredBacklinksPaginator.page = event.pageIndex + 1;
    this.discoveredBacklinksPaginator.offset = event.pageIndex * this.discoveredBacklinksPaginator.limit;
    await this.retrieveDiscoveredUrls();
  }

  async onSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.discoveredBacklinksPaginator.sort = `${event.direction === 'desc'
        ? '-'
        : ''}${event.active}`;
    } else if (event.direction === '') {
      this.discoveredBacklinksPaginator.sort = 'createdAt';
    }
    await this.retrieveDiscoveredUrls();
  }

  onQueryChange() {
    this.filtersSubject.next({ ...this.filters });
  }

  onDepthChange(event?: { value: Option }) {
    this.filters = { ...this.filters, depth: event?.value?.value };
    this.filtersSubject.next({ ...this.filters });
  }

  createdAt(discoveredUrl: DiscoveredUrl): string {
    if (discoveredUrl.createdAt) {
      return moment(discoveredUrl.createdAt).format('YYYY-MM-DD HH:mm');
    }
    return '-';
  }

  async deleteDocuments() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: { // TODO
        title: 'DOCUMENTS_LIST.DELETE_DOCUMENTS_TITLE',
        text: this.translate.instant(
          'DOCUMENTS_LIST.DELETE_DOCUMENTS_TEXT',
          { length: this.selection.selected.length }
        ),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(
      dialog.afterClosed()
    );
    if (result && result.confirmed) {
      try {
        this.loading = true;
        this._message.emitChange('LOADING', 'START');
        await this.discoveredUrlsService.deleteMany(
          this.selection.selected.map((doc) => doc._id!)
        );
        this.loading = false;
        this._message.emitChange('LOADING', 'END');
        this.toastr.success(
          this.translate.instant('DOCUMENTS_LIST.DOCUMENTS_DELETED',
            { length: this.selection.selected.length })
        );
        this.selection.clear();
        await this.retrieveDiscoveredUrls();
      } catch (err) {
        this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
        this._message.emitChange('LOADING', 'END');
        this.loading = false;
      }
    }
  }

  // selection
  hasPageSelection() {
    return this.discoveredBacklinksPaginator.docs.some(row => this.isSelected(row))
  }

  isSelected(row: DiscoveredUrl) {
    return this.selection.selected.map(doc => doc._id).includes(row._id)
  }

  isAllSelected() {
    return this.discoveredBacklinksPaginator.docs.every(row => this.isSelected(row));
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      const difference = _.differenceBy(
        this.selection.selected,
        this.discoveredBacklinksPaginator.docs, '_id'
      );
      this.selection.setSelection(...difference)
      this.selectionChange.emit(this.selection);
      return;
    }
    this.selection.setSelection(...[
      ...this.selection.selected,
      ...this.discoveredBacklinksPaginator.docs.filter(
        row => !this.isSelected(row)
      )]);
    this.selectionChange.emit(this.selection);
  }

  toggle(row: DiscoveredUrl) {
    if (this.isSelected(row)) {
      this.selection.setSelection(...this.selection.selected.filter(doc => doc._id !== row._id))
    } else {
      this.selection.setSelection(...[...this.selection.selected, row])
    }
    this.selectionChange.emit(this.selection);
  }
}
